import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Network, IndianRupee, Ban, RotateCcw } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { AdjustDialog } from "@/components/AdjustDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Panel, PageHead, StatusBadge, Empty } from "@/components/common";
import { TeamTree } from "@/components/TeamTree";
import { useApi, errMsg, inr, fmtDate } from "@/lib/api";

const TeamDialog = ({ member, onClose }) => {
  const api = useApi();
  const [data, setData] = useState(null);
  useEffect(() => { if (member) api("get", `/admin/members/${member.user_id}/team`).then(setData); else setData(null); }, [member, api]);
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto border-[#1F293D] bg-[#111827]" data-testid="member-team-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{member?.name}'s team</DialogTitle></DialogHeader>
        {data ? <TeamTree root={data.root} members={data.members} /> : <p className="text-sm text-slate-500">Loading…</p>}
      </DialogContent>
    </Dialog>
  );
};

export default function Members() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status !== "all") p.set("status", status);
    return api("get", `/admin/members${p.toString() ? `?${p}` : ""}`).then(setData);
  }, [api, q, status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const act = async (method, path, body, msg) => {
    try { await api(method, path, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-members-page">
      <PageHead label="Admin" title="Members">
        {data && <p className="font-mono text-sm text-slate-400">{data.members.length} shown</p>}
      </PageHead>
      <Panel>
        <Input data-testid="members-search-input" className="mb-4" placeholder="Search by name, email or referral code" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="mb-6 flex flex-wrap gap-2" data-testid="members-status-filters">
          {["all", "active", "pending", "inactive", "deactivated"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`members-filter-${s}`}
              className={`rounded-full border px-4 py-1.5 text-sm capitalize transition-colors ${status === s ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-[#1F293D] text-slate-400 hover:border-slate-500 hover:text-white"}`}>{s}</button>
          ))}
        </div>
        {!data ? <p className="text-sm text-slate-500">Loading…</p> : data.members.length === 0 ? <Empty testId="members-empty" text="No members found." /> : (
          <div className="overflow-x-auto">
            <Table data-testid="members-table">
              <TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Code</TableHead><TableHead>Sponsor</TableHead><TableHead>Directs</TableHead><TableHead>Wallet</TableHead><TableHead>Joined</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.members.map((m) => (
                  <TableRow key={m.user_id} data-testid={`member-row-${m.referral_code}`}>
                    <TableCell><p className="text-white">{m.name}{m.role === "admin" && <span className="ml-2 rounded border border-amber-500/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-400" data-testid={`admin-tag-${m.referral_code}`}>Admin</span>}</p><p className="text-xs text-slate-500">{m.email}</p></TableCell>
                    <TableCell className="font-mono text-xs">{m.referral_code}</TableCell>
                    <TableCell className="text-sm">{m.sponsor_name || "—"}</TableCell>
                    <TableCell className="font-mono">{m.direct}</TableCell>
                    <TableCell className="font-mono">{inr(m.wallet)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(m.created_at)}</TableCell>
                    <TableCell><StatusBadge status={m.status} testId={`member-status-${m.referral_code}`} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" data-testid={`adjust-wallet-btn-${m.referral_code}`} onClick={() => setAdjusting(m)}><IndianRupee className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" data-testid={`view-team-btn-${m.referral_code}`} onClick={() => setViewing(m)}><Network className="h-4 w-4" /></Button>
                        {m.status === "deactivated"
                          ? <Button size="sm" variant="outline" data-testid={`reactivate-btn-${m.referral_code}`} onClick={() => act("post", `/admin/members/${m.user_id}/reactivate`, {}, "Member reactivated")}><RotateCcw className="mr-1 h-4 w-4" />Reactivate</Button>
                          : <>
                              {m.status !== "active" && <Button size="sm" data-testid={`manual-activate-btn-${m.referral_code}`} onClick={() => act("post", `/admin/members/${m.user_id}/activate`, {}, "Member activated")}><Zap className="mr-1 h-4 w-4" />Activate</Button>}
                              {m.role !== "admin" && <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`deactivate-btn-${m.referral_code}`} onClick={() => setDeactivating(m)}><Ban className="h-4 w-4" /></Button>}
                            </>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
      <TeamDialog member={viewing} onClose={() => setViewing(null)} />
      <AdjustDialog member={adjusting} onClose={() => setAdjusting(null)} onDone={load} />
      <AlertDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
        <AlertDialogContent className="border-[#1F293D] bg-[#111827]" data-testid="deactivate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Deactivate {deactivating?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They can still sign in, but won't earn bonuses, withdraw or add money until you reactivate them. Their team and wallet stay as they are.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 text-white hover:bg-red-600" data-testid="deactivate-confirm-btn"
              onClick={() => { act("post", `/admin/members/${deactivating.user_id}/deactivate`, {}, "Member deactivated"); setDeactivating(null); }}>Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="mt-6 text-xs text-slate-500" data-testid="members-account-note">To permanently delete a member's login, or block them from signing in, use <span className="text-slate-300">Manage → Users &amp; Auth</span> in Emergent. Members reset their own password with <span className="text-slate-300">"Forgot password?"</span> on the login page.</p>
    </div>
  );
}
